import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { getDailyCloseHeads, getDailyCloseItems } from './apis'
import type { DailyCloseHead, DailyCloseHeadView, DailyCloseItem, DailyCloseItemSearch, DailyClosePage } from './types'

/** 탭 종료 시 MenuOnSessionWrap이 RP13 화면의 쿼리 캐시를 전량 제거할 수 있도록 메뉴 코드를 접두사로 둔다. */
const PATH = ['RP13', 'inventory', 'dailyClose'] as const

const isSameItemScope = (previous: DailyCloseItemSearch | null | undefined, current: DailyCloseItemSearch): boolean =>
  previous?.shipperCode === current.shipperCode
  && previous.closeDateFrom === current.closeDateFrom
  && previous.closeDateTo === current.closeDateTo
  && previous.warehouseName === current.warehouseName
  && previous.logisticsServiceType === current.logisticsServiceType

export const dailyCloseQueryKeys = {
  all: PATH,
  heads: (view: DailyCloseHeadView | null) => [...PATH, 'HEADS', view] as const,
  items: (params: DailyCloseItemSearch | null) => [...PATH, 'ITEMS', params] as const,
}

export const useGetDailyCloseHeads = (view: DailyCloseHeadView | null): UseQueryResult<DailyCloseHead[], Error> =>
  useQuery({
    queryKey: dailyCloseQueryKeys.heads(view),
    queryFn: async () => {
      if (!view) throw new Error('일마감 헤더 조회 조건이 없습니다.')
      return (await getDailyCloseHeads(view.search)).data.items
    },
    enabled: view != null,
  })

export const useGetDailyCloseItems = (params: DailyCloseItemSearch | null): UseQueryResult<DailyClosePage<DailyCloseItem>, Error> =>
  useQuery({
    queryKey: dailyCloseQueryKeys.items(params),
    queryFn: async () => {
      if (!params) throw new Error('일마감 상세 조회 조건이 없습니다.')
      return (await getDailyCloseItems(params)).data
    },
    enabled: params != null,
    placeholderData: params
      ? (previousData, previousQuery) => {
          const previousParams = previousQuery?.queryKey.at(-1) as DailyCloseItemSearch | null | undefined
          return isSameItemScope(previousParams, params) ? previousData : undefined
        }
      : undefined,
  })
